/**
 * The state model — one table, everything else derived from it.
 *
 * A state has three facets and they must never drift apart, so they live in a
 * single record per state:
 *
 *   digit       the units digit OpenHASP's firmware reads out of a style
 *               attribute's numeric suffix (see hasp_attribute_state.h in the
 *               firmware, which holds the identical table). Reachable only
 *               through stateSuffix()/STATE_DIGITS, used by wireFormat.js.
 *   label       what the Property Editor calls it.
 *   derivesFrom for a state a widget never declares itself, everything that has
 *               to hold before it is offered: FOCUSABLE, and/or the base states
 *               it combines. See expandStatesForSchema below.
 *
 * Every module that iterates, labels, validates or serializes states reads this
 * table rather than hardcoding names or digits positionally, so adding an LVGL
 * state is a single entry here — and, if it is a combination, nothing else at
 * all.
 */

/** Sentinel `derivesFrom` value: derived from the widget being focusable rather
 *  than from another state. */
export const FOCUSABLE = Symbol('focusable')

export const STATES = {
    default:               { digit: 0, label: 'Default' },
    checked:               { digit: 1, label: 'Checked' },
    pressed:               { digit: 2, label: 'Pressed' },
    pressedChecked:        { digit: 3, label: 'Pressed + Checked' },
    disabled:              { digit: 4, label: 'Disabled' },
    disabledChecked:       { digit: 5, label: 'Disabled + Checked' },
    focused:               { digit: 6, label: 'Focused',                     derivesFrom: [FOCUSABLE] },
    focusedPressed:        { digit: 7, label: 'Focused + Pressed',           derivesFrom: [FOCUSABLE, 'pressed'] },
    focusedChecked:        { digit: 8, label: 'Focused + Checked',           derivesFrom: [FOCUSABLE, 'checked'] },
    focusedPressedChecked: { digit: 9, label: 'Focused + Pressed + Checked', derivesFrom: [FOCUSABLE, 'pressedChecked'] },
}

// There is deliberately no focusedDisabled: LVGL resolves that combination
// without one, because it prefers the candidate whose state bits are the
// largest subset of the requested state and LV_STATE_DISABLED (0x20) outranks
// LV_STATE_FOCUSED (0x02) — so a `disabled` rule already wins on an object that
// is both. LV_STATE_CHECKED (0x01) is the other way round, it loses to focused,
// which is exactly why focusedChecked does need an entry. Digits 0-9 are full.

export const CANONICAL_STATES = Object.keys(STATES)

export const STATE_LABELS = Object.fromEntries(
    Object.entries(STATES).map(([name, def]) => [name, def.label]),
)

export const STATE_DIGITS = Object.fromEntries(
    Object.entries(STATES).map(([name, def]) => [name, def.digit]),
)

/** The states a schema may declare for itself. The rest are derived, and
 *  schemaValidator rejects them if one is written by hand. */
export const BASE_STATES = CANONICAL_STATES.filter(name => !STATES[name].derivesFrom)

/**
 * The two-character suffix OpenHASP's firmware protocol uses for a state, or ''
 * for the default state. Zero-padded, because the legacy per-state theme shape
 * keys off exactly this string (`theme.states.states01`).
 */
export function stateSuffix(state) {
    const digit = STATE_DIGITS[state]
    return digit ? String(digit).padStart(2, '0') : ''
}

/**
 * The full list of states a widget offers, derived from what its schema
 * declares. Takes the schema rather than its pieces, so `focusable` never has to
 * be threaded through UI or resolver code — it is read here and nowhere else.
 *
 * A derived state is offered only when every one of its prerequisites holds, so
 * a widget without `checked` never offers `focusedChecked`, and an unfocusable
 * one offers no focused state at all. Focusability holds for every widget except
 * a screen: LVGL's lv_obj_create() sets click = 1 on every child object, so it is
 * hit-tested and click-focused, while a screen is memset to zero and never sets
 * click, so it can never receive LV_SIGNAL_FOCUS.
 *
 * @param schema a v2 object schema; `states` defaults to none, `focusable` to true
 * @returns {string[]} declared states first, then derived ones; never 'default'
 */
export function expandStatesForSchema(schema) {
    // A derived state written into a schema by hand is dropped rather than
    // duplicated; schemaValidator reports it so it gets removed at the source.
    const base      = (schema?.states ?? []).filter(name => !STATES[name]?.derivesFrom)
    const focusable = schema?.focusable !== false

    // Focusability, not interactivity: a widget that styles no other state is
    // still hit-tested and click-focused, so it still offers `focused`. Gating
    // this on the widget declaring some other state would drop the focus ring
    // from exactly the widgets that style nothing else (chart, img, qrcode).
    const satisfied = prerequisite => (
        prerequisite === FOCUSABLE ? focusable : base.includes(prerequisite)
    )

    const derived = CANONICAL_STATES.filter(name => {
        const from = STATES[name].derivesFrom
        return from ? from.every(satisfied) : false
    })

    return [...base, ...derived]
}

/**
 * Every schema-level normalization the registry owes its consumers, so that
 * `schema.states` is already the full derived list everywhere downstream and no
 * consumer ever has to read `focusable`.
 *
 * `baseStates` keeps what the schema actually declared, which is what
 * schemaValidator has to look at to tell a hand-written derived state from one
 * this function added.
 */
export function normalizeSchema(schema) {
    return { ...schema, baseStates: schema?.states ?? [], states: expandStatesForSchema(schema) }
}
